import { GANTT_LAYOUT_PRESETS } from "@/ds";
import {
  formatarIdentificacaoObra,
  identificacaoDaObra,
  textoDaObra,
  type IdentificacaoObra,
  type ObraIdentificavel,
} from "@/shared/utils/formatar-identificacao-obra";
import {
  assignLanes,
  calcularConflitos,
  durationInDays,
  formatDate,
  formatDayMonth,
  formatDurationShort,
  isObraCancelada,
  isPast,
  positionInterval,
  separarConflitos,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoConflitoIgnorado,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
  CargaTrabalhoProfissionaisData,
  CargaTrabalhoProfissional,
  ConflitoAlocacao,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import type { ConflictGroup } from "./gantt-local-rows";

export type SegmentKind = "planejamento" | "obra" | "fechamento" | "cancelada" | "transferida";

export interface GanttSegment {
  kind: SegmentKind;
  left: number;
  width: number;
}

/** Pílula de duração dentro da linha da obra: posição e largura em % da linha. */
export interface GanttPill {
  left: number;
  width: number;
  label: string;
}

export interface GanttItem {
  key: string;
  kind: "bar" | "indisponibilidade";
  lane: number;
  left: number;
  width: number;
  /** `ID - sigla`: o `title` e o `aria-label` da barra, como os tooltips (ADR 0018). */
  label: string;
  /** Nome e sigla da obra: o texto visível da pílula, que o `RotuloObra` escolhe pela largura (ADR 0018). */
  rotulo?: IdentificacaoObra;
  past: boolean;
  cancelada: boolean;
  /** Obra que passará ao substituto, na revisão de "Resolver Conflito" (HU 2434). */
  transferida?: boolean;
  segments: GanttSegment[];
  /** Data de início formatada (DD/MM), para a barra em linha do Gantt de Fornecedor. */
  start?: string;
  /** Data de fim formatada (DD/MM). */
  end?: string;
  /** Duração resumida (ex.: "1m", "15d"). */
  duration?: string;
  /** Dias da indisponibilidade; o VM da barra decide se cabe o texto ou só o ícone. */
  dias?: number;
  /** Pílula do escopo macro dentro da linha (Gantt por Obra). */
  pill?: GanttPill;
  /** Equivalente textual do tooltip, uma informação por linha (acessibilidade e fallback). */
  tooltip: string;
  /** Card do tooltip (Figma: tooltip-gantt-datas); ausente, vale o texto de `tooltip`. */
  card?: GanttTipCard;
}

/** Cor da bolinha de uma linha do card: as etapas do Gantt e a indisponibilidade. */
export type GanttTipTone = "planejamento" | "obra" | "fechamento" | "indisponibilidade";

/** Linha do card: bolinha, rótulo, intervalo em negrito e duração à direita. */
export interface GanttTipPhase {
  label: string;
  tone: GanttTipTone;
  /** `dd/mm/aaaa – dd/mm/aaaa`. */
  periodo: string;
  /** `N dias`. */
  dias: string;
}

/** Linha sem bolinha, só rótulo e valor (variante Cancelada). */
export interface GanttTipFact {
  label: string;
  value: string;
}

/** Conteúdo estruturado do tooltip de uma barra do Gantt. */
export interface GanttTipCard {
  /** `Obra 24 · A. Abrolhos`. */
  title: string;
  /** `Profissional · Papel`; `null` quando não há recurso na linha. */
  subtitle: string | null;
  /** Selo ao lado do título, como "Cancelada". */
  badge?: string;
  /** Cor do selo: `danger` (Cancelada, padrão), `neutral` ou `warn` (Simulação). */
  badgeTone?: "danger" | "neutral" | "warn";
  phases: GanttTipPhase[];
  /** "Período total": do início da primeira ao fim da última etapa. */
  total?: { periodo: string; dias: string };
  facts?: GanttTipFact[];
}

export interface GanttBand {
  key: string;
  left: number;
  width: number;
  tooltip: string;
  info?: ConflictInfo;
  /** Card rico do conflito (obras e datas), quando a faixa usa `ConflictBand`. */
  groups?: ConflictGroup[];
  /** RN07 (HU 2434): conflito mantido com justificativa — destaque de situação tratada. */
  ignored?: boolean;
  /** Período em destaque na revisão do remanejamento (HU 2434). */
  highlight?: boolean;
}

/** Conflito em campos prontos para exibição (datas em DD/MM/AAAA). */
export interface ConflictInfo {
  primeira: string;
  segunda: string;
  inicio: string;
  fim: string;
}

export interface GanttRowModel {
  key: string;
  kind: "profissional";
  title: string;
  subtitle: string | null;
  meta: string | null;
  /** Quantidade de conflitos em aberto (os ignorados não entram na tag). */
  conflicts: number;
  conflictTooltip: string;
  /** Card do tooltip do selo de conflito (Figma 10357:86888): um grupo por conflito. */
  conflictGroups?: ConflictGroup[];
  conflictInfos?: ConflictInfo[];
  /** Conflitos em aberto, na ordem do cronograma: insumo de "Resolver Conflito". */
  conflitos: ConflitoAlocacao[];
  profissional: CargaTrabalhoProfissional;
  items: GanttItem[];
  bands: GanttBand[];
  lanes: number;
}

export const NO_TEAM_LABEL = "Sem equipe definida";

const KIND_LABEL: Record<SegmentKind, string> = {
  planejamento: "Planejamento",
  obra: "Obra",
  fechamento: "Fechamento",
  cancelada: "Cancelada",
  transferida: "Obra Transferida",
};

/** `ID - nome da embarcação` (ADR 0018), no texto de conflito e no resumo da revisão. */
export function obraLabel(obra: Pick<CargaTrabalhoObra, "codigo" | "nome" | "embarcacao">): string {
  return textoDaObra(obra);
}

/** `label` e `rotulo` da pílula de uma obra (ADR 0018). */
export function pilulaDaObra(obra: ObraIdentificavel): Pick<GanttItem, "label" | "rotulo"> {
  return { label: formatarIdentificacaoObra(obra), rotulo: identificacaoDaObra(obra) };
}

export function toConflictInfo(conflito: ConflitoAlocacao): ConflictInfo {
  return {
    primeira: obraLabel(conflito.a.obra),
    segunda: obraLabel(conflito.b.obra),
    inicio: formatDate(conflito.inicio),
    fim: formatDate(conflito.fim),
  };
}

/**
 * Linhas do card de conflito: a obra que começa depois "inicia em" a sobreposição; a outra
 * "termina em" (mesma leitura do Local, `describeConflito`).
 */
export function toConflictGroup(conflito: ConflitoAlocacao): ConflictGroup {
  const [later, other] =
    conflito.a.inicio >= conflito.b.inicio ? [conflito.a, conflito.b] : [conflito.b, conflito.a];
  return {
    lines: [
      {
        label: `Obra ${formatarIdentificacaoObra(later.obra)}`,
        event: `inicia em ${formatDate(later.inicio)}`,
        tone: "start",
      },
      {
        label: `Obra ${formatarIdentificacaoObra(other.obra)}`,
        event: `termina em ${formatDate(other.fim)}`,
        tone: "end",
      },
    ],
  };
}

export function rangeLine(label: string, intervalo: CargaTrabalhoIntervalo): string {
  const days = durationInDays(intervalo);
  return `${label}: ${formatDate(intervalo.inicio)} a ${formatDate(intervalo.fim)} (${days} ${days === 1 ? "dia" : "dias"})`;
}

/** `dd/mm/aaaa – dd/mm/aaaa`. */
export function periodoText(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDate(intervalo.inicio)} – ${formatDate(intervalo.fim)}`;
}

/** `N dias` (ou `1 dia`). */
export function diasText(intervalo: CargaTrabalhoIntervalo): string {
  const days = durationInDays(intervalo);
  return `${days} ${days === 1 ? "dia" : "dias"}`;
}

/** `Obra 24 - PSV`. */
export function obraCardTitle(obra: ObraIdentificavel): string {
  return `Obra ${formatarIdentificacaoObra(obra)}`;
}

/** `Profissional · Papel`; sem papel, só o nome. */
export function recursoLabel(nome: string, papel: string | null | undefined): string {
  return papel ? `${nome} · ${papel}` : nome;
}

/** Equivalente textual do card, uma informação por linha (title/aria e fallback). */
export function tipCardToText(card: GanttTipCard): string {
  const lines = [card.badge ? `${card.title} (${card.badge})` : card.title];
  if (card.subtitle) lines.push(card.subtitle);
  for (const phase of card.phases) {
    lines.push(`${phase.label}: ${phase.periodo} (${phase.dias})`);
  }
  for (const fact of card.facts ?? []) lines.push(`${fact.label}: ${fact.value}`);
  if (card.total) lines.push(`Período total: ${card.total.periodo} (${card.total.dias})`);
  return lines.join("\n");
}

/** Extremos (menor início, maior fim) de uma lista de intervalos. */
function envelope(intervalos: CargaTrabalhoIntervalo[]): CargaTrabalhoIntervalo {
  const inicios = intervalos.map((item) => item.inicio).sort();
  const fins = intervalos.map((item) => item.fim).sort();
  return { inicio: inicios[0] ?? "", fim: fins[fins.length - 1] ?? "" };
}

interface ObraBarInput {
  key: string;
  obra: CargaTrabalhoObra;
  /** Intervalo de alocação do profissional (ou `null` para a barra da obra). */
  alocacao: CargaTrabalhoIntervalo | null;
  /** Subtítulo do card: `Profissional · Papel`. */
  recurso?: string;
}

/** Obra cancelada só aparece com o indicador "Cancelada" ligado; as demais sempre. */
export function isAlocacaoVisivel(
  obra: Pick<CargaTrabalhoObra, "situacao">,
  indicators: GanttIndicators,
): boolean {
  return indicators.cancelada || !isObraCancelada(obra);
}

/** Monta a barra de uma obra com as etapas visíveis; `null` quando nada aparece. */
export function buildObraItem(
  input: ObraBarInput,
  timeline: Timeline,
  indicators: GanttIndicators,
  today: IsoDate,
): Omit<GanttItem, "lane"> | null {
  const { obra, alocacao } = input;
  if (!isAlocacaoVisivel(obra, indicators)) return null;
  const cancelada = isObraCancelada(obra);
  const fallback = alocacao ?? obra.etapas.obra ?? null;
  const title = obraCardTitle(obra);
  const subtitle = input.recurso ?? null;

  if (cancelada) {
    const intervalo = alocacao ?? obra.etapas.obra ?? obra.etapas.planejamento;
    if (!intervalo) return null;
    const position = positionInterval(intervalo, timeline);
    if (!position) return null;
    // Figma (tooltip-gantt-cancelada): "Cancelada em/por" e "Motivo" não existem no contrato.
    const canceladaCard: GanttTipCard = {
      title,
      subtitle,
      badge: "Cancelada",
      phases: [],
      facts: [{ label: "Período previsto", value: periodoText(intervalo) }],
    };
    return {
      key: input.key,
      kind: "bar",
      left: position.left,
      width: position.width,
      ...pilulaDaObra(obra),
      start: formatDayMonth(intervalo.inicio),
      end: formatDayMonth(intervalo.fim),
      duration: formatDurationShort(intervalo),
      past: false,
      cancelada: true,
      segments: [{ kind: "cancelada", left: 0, width: 100 }],
      tooltip: tipCardToText(canceladaCard),
      card: canceladaCard,
    };
  }

  const stages: {
    kind: Exclude<SegmentKind, "cancelada" | "transferida">;
    intervalo: CargaTrabalhoIntervalo;
  }[] = [];
  const etapas = obra.etapas;
  if (etapas.planejamento) stages.push({ kind: "planejamento", intervalo: etapas.planejamento });
  if (etapas.obra) stages.push({ kind: "obra", intervalo: etapas.obra });
  else if (fallback) stages.push({ kind: "obra", intervalo: fallback });
  if (etapas.fechamento) stages.push({ kind: "fechamento", intervalo: etapas.fechamento });

  const total = envelope(stages.map((stage) => stage.intervalo));
  const card: GanttTipCard = {
    title,
    subtitle,
    phases: stages.map(({ kind, intervalo }) => ({
      label: KIND_LABEL[kind],
      tone: kind,
      periodo: periodoText(intervalo),
      dias: diasText(intervalo),
    })),
    ...(stages.length > 0 ? { total: { periodo: periodoText(total), dias: diasText(total) } } : {}),
  };
  const visible = stages.filter((stage) => indicators[stage.kind]);
  if (visible.length === 0) return null;

  const outer = positionInterval(envelope(visible.map((stage) => stage.intervalo)), timeline);
  if (!outer) return null;

  const segments: GanttSegment[] = [];
  for (const stage of visible) {
    const position = positionInterval(stage.intervalo, timeline);
    if (!position) continue;
    segments.push({
      kind: stage.kind,
      left: ((position.left - outer.left) / outer.width) * 100,
      width: (position.width / outer.width) * 100,
    });
  }
  if (segments.length === 0) return null;

  // A barra é contínua: o vão entre etapas vizinhas (fim de semana, recesso) não deixa buraco.
  const order: SegmentKind[] = ["planejamento", "obra", "fechamento"];
  segments.forEach((segment, index) => {
    const next = segments[index + 1];
    if (!next || order.indexOf(next.kind) !== order.indexOf(segment.kind) + 1) return;
    segment.width = Math.max(segment.width, next.left - segment.left);
  });

  // Só depois do laço acima, que supõe a ordem cronológica. A Obra vira a base e as pontas ficam por
  // cima; o `sort` é estável, então Planejamento segue antes de Fechamento.
  segments.sort((a, b) => (a.kind === "obra" ? -1 : b.kind === "obra" ? 1 : 0));

  return {
    key: input.key,
    kind: "bar",
    left: outer.left,
    width: outer.width,
    ...pilulaDaObra(obra),
    start: formatDayMonth(total.inicio),
    end: formatDayMonth(total.fim),
    duration: formatDurationShort(total),
    past: isPast(total, today),
    cancelada: false,
    segments,
    tooltip: tipCardToText(card),
    card,
  };
}

export const CONFLICT_TITLE = "Conflito detectado";
export const IGNORED_CONFLICT_TITLE = "Conflito ignorado";
export const SIMULACAO_TIP_BADGE = "Simulação";

/** Inclui o badge Simulação no card da barra quando a linha tem marca, sem cobrir "Cancelada". */
export function withSimulacaoBadge(
  card: GanttTipCard | undefined,
  marcado: boolean,
): GanttTipCard | undefined {
  if (!card || !marcado || card.badge) return card;
  return { ...card, badge: SIMULACAO_TIP_BADGE, badgeTone: "warn" };
}

export function conflictBands(
  conflitos: ConflitoAlocacao[],
  timeline: Timeline,
  prefix: string,
  ignored = false,
): { bands: GanttBand[]; tooltip: string } {
  const title = ignored ? IGNORED_CONFLICT_TITLE : CONFLICT_TITLE;
  const bands: GanttBand[] = [];
  const lines: string[] = [];
  conflitos.forEach((conflito, index) => {
    const detail = [
      `Obra ${formatarIdentificacaoObra(conflito.a.obra)}`,
      `Obra ${formatarIdentificacaoObra(conflito.b.obra)}`,
      rangeLine("Sobreposição", conflito),
    ].join("\n");
    lines.push(detail);
    const position = positionInterval(conflito, timeline);
    if (!position) return;
    bands.push({
      key: `${prefix}-${index}`,
      left: position.left,
      width: position.width,
      tooltip: `${title}
${detail}`,
      info: toConflictInfo(conflito),
      ...(ignored ? { ignored, groups: [toConflictGroup(conflito)] } : {}),
    });
  });
  return {
    bands,
    tooltip: lines.length > 0 ? `${title}\n${lines.join("\n\n")}` : "",
  };
}

/** Largura mínima da pílula da indisponibilidade: o `min-width` de `$ds-spacing-8` (2rem) em `.indisponibilidade`. */
export const INDISPONIBILIDADE_MIN_PX = 32;
/** Rótulo externo "Nd" à direita da pílula estreita: respiro de 4px ($ds-spacing-1) + "999d" em 12px Medium (~28px). */
export const ROTULO_EXTERNO_PX = 32;
/** Dias por mês na conversão dias → px (o mês do Gantt tem largura fixa, não de calendário). */
export const DIAS_POR_MES = 30.4;

/**
 * Dias que a indisponibilidade reserva ao distribuir as faixas: a pílula (ao menos 2rem) mais o rótulo externo. A conta
 * usa o mês mobile do preset `compact` (3,8125rem = ~2,0px/dia), o mais estreito e o que pede mais dias; no desktop
 * (6rem, ~3,16px/dia) sobra folga. Ex.: 5 dias = (32 + 32) / 2,0 = 32; 18 dias = (36 + 32) / 2,0 = 34; 32 dias = 48.
 * Par de `LARGURA_MINIMA_COM_TEXTO_PX` (`use-gantt-allocation-bar.ts`); ambos partem de `GANTT_LAYOUT_PRESETS.compact`.
 */
export function diasReservadosDaIndisponibilidade(dias: number): number {
  const pxPorDia = (GANTT_LAYOUT_PRESETS.compact.monthWidthMobileRem * 16) / DIAS_POR_MES;
  return Math.ceil(
    (Math.max(dias * pxPorDia, INDISPONIBILIDADE_MIN_PX) + ROTULO_EXTERNO_PX) / pxPorDia,
  );
}

function layoutItems(
  entries: { item: Omit<GanttItem, "lane">; intervalo: CargaTrabalhoIntervalo }[],
): { items: GanttItem[]; lanes: number } {
  const lanes = assignLanes(
    entries.map((entry) => entry.intervalo),
    entries.map((entry) =>
      entry.item.kind === "indisponibilidade" && entry.item.dias !== undefined
        ? diasReservadosDaIndisponibilidade(entry.item.dias)
        : 1,
    ),
  );
  const items = entries.map((entry, index) => ({ ...entry.item, lane: lanes[index] ?? 0 }));
  return { items, lanes: Math.max(1, ...lanes.map((lane) => lane + 1)) };
}

/**
 * Perspectiva Profissionais agrupada por profissional. `ignoradosLocais` soma os
 * conflitos ignorados nesta sessão aos que o bff já informa (RN07).
 */
export function buildProfissionalRows(
  data: CargaTrabalhoProfissionaisData,
  timeline: Timeline,
  indicators: GanttIndicators,
  today: IsoDate,
  ignoradosLocais: CargaTrabalhoConflitoIgnorado[] = [],
  /** Restringe o que conta como conflito (Fornecedor: só entre obras distintas, HU 2041). */
  conflitoFilter: (conflito: ConflitoAlocacao) => boolean = () => true,
): GanttRowModel[] {
  return data.profissionais.map((profissional) => {
    const entries: { item: Omit<GanttItem, "lane">; intervalo: CargaTrabalhoIntervalo }[] = [];

    for (const alocacao of profissional.alocacoes) {
      const item = buildObraItem(
        {
          key: alocacao.id,
          obra: alocacao.obra,
          alocacao,
          recurso: recursoLabel(profissional.nome, profissional.time),
        },
        timeline,
        indicators,
        today,
      );
      if (item) entries.push({ item, intervalo: spanOf(item, timeline) });
    }

    if (indicators.indisponibilidade) {
      profissional.indisponibilidades.forEach((intervalo, index) => {
        const position = positionInterval(intervalo, timeline);
        if (!position) return;
        const days = durationInDays(intervalo);
        const card: GanttTipCard = {
          title: "Indisponibilidade",
          subtitle: recursoLabel(profissional.nome, profissional.time),
          phases: [
            {
              label: "Período",
              tone: "indisponibilidade",
              periodo: periodoText(intervalo),
              dias: diasText(intervalo),
            },
          ],
        };
        entries.push({
          item: {
            key: `${profissional.id}-indisp-${index}`,
            kind: "indisponibilidade",
            left: position.left,
            width: position.width,
            label: `${days}d`,
            dias: days,
            past: isPast(intervalo, today),
            cancelada: false,
            segments: [],
            tooltip: tipCardToText(card),
            card,
          },
          intervalo: spanOf({ left: position.left, width: position.width }, timeline),
        });
      });
    }

    const { abertos, ignorados } = separarConflitos(
      calcularConflitos(profissional.alocacoes).filter(conflitoFilter),
      [...(profissional.conflitosIgnorados ?? []), ...ignoradosLocais],
    );
    const { bands, tooltip } = conflictBands(abertos, timeline, profissional.id);
    const ignoredBands = conflictBands(ignorados, timeline, `${profissional.id}-ignorado`, true);
    const { items, lanes } = layoutItems(entries);
    // O contador segue os indicadores, não a janela do timeline: alocação fora dela continua contando.
    const total = profissional.alocacoes.filter((alocacao) =>
      isAlocacaoVisivel(alocacao.obra, indicators),
    ).length;

    return {
      key: profissional.id,
      kind: "profissional" as const,
      title: profissional.nome,
      subtitle: profissional.time,
      meta: `${total} ${total === 1 ? "Alocação" : "Alocações"}`,
      conflicts: abertos.length,
      conflictTooltip: tooltip,
      conflictGroups: abertos.map(toConflictGroup),
      conflictInfos: abertos.map(toConflictInfo),
      conflitos: abertos,
      profissional,
      items,
      // A faixa verde (ignorado) vai por último: pintada antes, a laranja a cobriria.
      bands: [...bands, ...ignoredBands.bands],
      lanes,
    };
  });
}

/** Converte a posição em % de volta para dias, só para empacotar as faixas. */
function spanOf(
  position: { left: number; width: number },
  timeline: Timeline,
): CargaTrabalhoIntervalo {
  const start = Math.round((position.left / 100) * timeline.totalDays);
  const end = Math.round(((position.left + position.width) / 100) * timeline.totalDays) - 1;
  return {
    inicio: dayToIso(timeline.startDay + start),
    fim: dayToIso(timeline.startDay + Math.max(start, end)),
  };
}

function dayToIso(day: number): IsoDate {
  const date = new Date(day * 86_400_000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
